import React, { type FC, useEffect, useMemo } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { BarChartSquare01, Building02, Building07, Monitor02 } from '@flipdish/ui-icons';
import type { CommandMenuGroupType } from '@flipdish/ui-library/components/organisms/CommandMenu';
import clsx from 'clsx';
import Fuse from 'fuse.js';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';

import { ONE_SECOND } from '../../../../helpers/timeConstants';
import { customActions } from '../../../../services/datadogRUM/customActions';
import { shouldDisplay, usePortalUser } from '../../../../viewConfiguration';
import useSearchMenuItems from '../../Search/customHooks/useSearchMenuItems';
import { fuseOptions, getPropertyAndSalesChannelInfo } from '../../Search/SettingsSearchBar';
import { subItemIconOverrides, topLevelIconOverrides } from '../../useSidebarNavItems';

/** CommandMenu does not virtualise its list; the design system asks for about 50 rows. */
export const MAX_COMMAND_MENU_RESULTS = 50;
export const COMMAND_MENU_GROUP_ID = 'results';

type CommandMenuItem = CommandMenuGroupType['items'][number];
type SearchMenuItem = ReturnType<typeof useSearchMenuItems>['menuItems'][number];
/** A permitted search item with its stable id and the combined keys the Fuse index searches. */
type IndexedMenuItem = SearchMenuItem & {
  id: string;
  searchByTitlePropertyAndId: string;
  searchByPropertyAndIdAndTitle: string;
  searchByIdAndTitleAndProperty: string;
  searchByChannelNameAndIdAndTitle: string;
  searchByIdAndChannelNameAndTitle: string;
  searchByChannelNameAndTitleAndId: string;
  searchByTitleAndChannelNameAndType: string;
};

type NavIcon = FC<{ className?: string }>;

/** Wraps a rendered icon element into the component type CommandMenu.Item expects. */
const wrapIcon = (element: React.ReactElement<{ className?: string }>): NavIcon => {
  const WrappedIcon: NavIcon = ({ className }) =>
    React.cloneElement(element, { className: clsx(element.props.className, className) });
  return WrappedIcon;
};

/**
 * The Untitled icon for a search item: the sidebar's icon for the same menu entry, a category icon
 * for property, sales-channel, report and organisation items, else the item's own (MUI) icon.
 * TODO: read the icon from the item once the legacy sidebar is gone and menuItemsConfig carries them.
 */
export const resolveItemIcon = (item: SearchMenuItem): NavIcon | undefined => {
  const key = 'titleKey' in item ? item.titleKey : undefined;
  const override = key ? (subItemIconOverrides[key] ?? topLevelIconOverrides[key]) : undefined;
  if (override) return override;
  const { propertyId, salesChannelId } = getPropertyAndSalesChannelInfo(item);
  if (salesChannelId) return Monitor02;
  if (propertyId) return Building02;
  if (item.path?.includes('/reports')) return BarChartSquare01;
  if (item.path?.includes('/settings/organisation')) return Building07;
  return React.isValidElement<{ className?: string }>(item.icon) ? wrapIcon(item.icon) : undefined;
};

/** The two caption lines the legacy dropdown showed, joined into CommandMenu's single description. */
const getDescription = (item: SearchMenuItem) => {
  const { propertyName, propertyId, salesChannelName, salesChannelId, salesChannelType } =
    getPropertyAndSalesChannelInfo(item);
  const lines = [
    propertyName && propertyId ? `${propertyName} • ${propertyId}` : '',
    salesChannelName && salesChannelId
      ? `${salesChannelName} • ${salesChannelId} • ${salesChannelType}`
      : '',
  ].filter(Boolean);
  return lines.length ? lines.join(' • ') : undefined;
};

const getItemId = (item: SearchMenuItem) => {
  const { propertyId, salesChannelId } = getPropertyAndSalesChannelInfo(item);
  return [item.path, propertyId, salesChannelId].filter(Boolean).join('|');
};

/**
 * Gives every permitted item an id before any query runs, so a row keeps its key, focus and derived
 * test id while the user types. The rare exact duplicates get a counter in list order.
 */
const withStableIds = <T extends SearchMenuItem>(items: T[]) => {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const baseId = getItemId(item);
    const count = seen.get(baseId) ?? 0;
    seen.set(baseId, count + 1);
    return { ...item, id: count ? `${baseId}#${count}` : baseId };
  });
};

const toCommandMenuItem = (
  item: SearchMenuItem & { id: string },
  onSelect: (path: string) => void
): CommandMenuItem => {
  const description = getDescription(item);
  const base = {
    id: item.id,
    label: item.title,
    href: item.path,
    onAction: () => onSelect(item.path),
    ...(description ? { description, stacked: true } : {}),
  };
  const icon = resolveItemIcon(item);
  return icon ? { ...base, type: 'icon', icon } : { ...base, type: 'default' };
};

/**
 * Every item the legacy search dropdown shows, as one CommandMenu group, filtered with the same
 * Fuse index as `SettingsSearchBar` and capped at `MAX_COMMAND_MENU_RESULTS`.
 */
export const useCommandMenuItems = (query: string): CommandMenuGroupType[] => {
  const { menuItems } = useSearchMenuItems();
  const portalUser = usePortalUser();

  // Apply the central display decision before indexing search results.
  const permittedMenuItems = useMemo<IndexedMenuItem[]>(
    () =>
      withStableIds(menuItems)
        .filter((item) =>
          shouldDisplay(portalUser, item.viewId, {
            legacyVisibilityPermissions: item.permissions,
            exemptFromViewConfiguration: item.exemptFromViewConfiguration,
          })
        )
        .map((item) => {
          const { propertyName, propertyId, salesChannelName, salesChannelId, salesChannelType } =
            getPropertyAndSalesChannelInfo(item);
          return {
            // Same combined keys as SettingsSearchBar, so fuzzy matching behaves the same.
            ...item,
            searchByTitlePropertyAndId: `${item.title ?? ''} ${propertyName} ${propertyId}`,
            searchByPropertyAndIdAndTitle: `${propertyName} ${propertyId} ${item.title ?? ''}`,
            searchByIdAndTitleAndProperty: `${propertyId} ${item.title ?? ''} ${propertyName} `,
            searchByChannelNameAndIdAndTitle: `${salesChannelName} ${salesChannelId} ${item.title ?? ''}`,
            searchByIdAndChannelNameAndTitle: `${salesChannelId} ${salesChannelName} ${item.title ?? ''}`,
            searchByChannelNameAndTitleAndId: `${salesChannelName} ${item.title ?? ''} ${salesChannelId}`,
            searchByTitleAndChannelNameAndType: `${item.title ?? ''} ${salesChannelName} ${salesChannelType}`,
          };
        }),
    [menuItems, portalUser]
  );

  const fuse = useMemo<Fuse<IndexedMenuItem>>(
    () => new Fuse(permittedMenuItems, fuseOptions),
    [permittedMenuItems]
  );

  // `total` is the full match count, as the legacy search reported it; `results` is what the menu lists.
  const { results, total } = useMemo<{ results: IndexedMenuItem[]; total: number }>(() => {
    const trimmed = query.trim();
    const matches = trimmed
      ? fuse.search(trimmed).map((result) => result.item)
      : permittedMenuItems;
    return { results: matches.slice(0, MAX_COMMAND_MENU_RESULTS), total: matches.length };
  }, [query, fuse, permittedMenuItems]);

  const reportSearch = useMemo<DebouncedFunc<(searchValue: string, resultCount: number) => void>>(
    () =>
      debounce((searchValue: string, resultCount: number) => {
        datadogRum.addAction(customActions.SEARCH_PERFORMED, { searchValue, resultCount });
      }, ONE_SECOND * 0.75),
    []
  );

  useEffect(() => {
    if (query.trim()) {
      reportSearch(query, total);
    }
    return () => reportSearch.cancel();
  }, [query, total, reportSearch]);

  return useMemo<CommandMenuGroupType[]>(() => {
    const onSelect = (path: string) => {
      datadogRum.addAction(customActions.SEARCH_RESULT_CLICKED, {
        searchValue: query,
        resultCount: total,
        result: path,
      });
    };
    return [
      {
        id: COMMAND_MENU_GROUP_ID,
        items: results.map((item) => toCommandMenuItem(item, onSelect)),
      },
    ];
  }, [results, total, query]);
};

export default useCommandMenuItems;
